import { fork, cancel, take, select, call, put, delay } from 'redux-saga/effects';
import type { Task } from '@redux-saga/types';
import type { Dispatch } from 'redux';
import { z } from 'zod';
import { Map } from 'immutable';
import { WSClientFactory } from '@/sagas/ws';
import { getRuntimeConfig } from '@/lib/runtimeConfig';
import { signInAction, signOutAction, chatTokenSelector, userIdSelector, userSelector } from '@/domain/env';
import { ghostIdentities, setUnreadFromChannelsAction, isNewChatSelector } from '@/domain/chat';
import { getOrganizationFeatures } from '@/domain/organization/organizationActions';
import { chatNotificationsMessageHandler } from '@/domain/ws/handlers';
import { locationSelector } from '@/domain/router/RouterSelector';
import { getUnreadVisitKey, isListVisitKey } from '@/domain/router/utils';
import { LOCATION_CHANGE } from '@/domain/router/redux/reduxActions';
import Api from '@/domain/api';

// Verified /unread shape (captured live):
//   unread_companies:  { [companyId]: totalCount }
//   unread_documents:  { [companyId]: { [documentId]: count } }   ← per-document COUNT
//   unread_messages:   grand total
const chatUnreadCountsSchema = z.object({
  unread_companies: z.record(z.string(), z.number()).default({}),
  unread_documents: z.record(z.string(), z.record(z.string(), z.number())).default({}),
  unread_messages: z.number().default(0),
});

function* createContext() {
  const { userId } = (yield select(userSelector)) as { userId: string };
  const hub = getRuntimeConfig().env.REACT_APP_PUBSUB_NOTIFICATIONS_HUB;
  return {
    hub,
    user_context: {
      user_id: userId,
      page: 'chat_notifications',
    },
  };
}

const RETRY_DELAY_MS = 2000;
const MAX_RETRIES = 4; // 5 total attempts

function* fetchUnreadCounts(retriesLeft = MAX_RETRIES): Generator {
  try {
    const { data } = (yield call(Api.getChatUnreadCounts)) as { data: unknown };
    const parsed = chatUnreadCountsSchema.parse(data);
    const unreadCompanies = Map(parsed.unread_companies);
    // Map<companyId, Map<documentId, count>> — per-document unread counts.
    const unreadDocuments = Map(parsed.unread_documents).map((docs) => Map(docs));
    yield put(
      setUnreadFromChannelsAction({
        unreadCompanies,
        unreadDocuments,
        totalUnreadCount: parsed.unread_messages,
      }),
    );
  } catch (err) {
    if (retriesLeft > 0) {
      yield delay(RETRY_DELAY_MS);
      yield call(fetchUnreadCounts, retriesLeft - 1);
    } else {
      console.error('[chatNotifications] Failed to fetch unread counts after all retries:', err);
    }
  }
}

// Refetch the unread counts on genuine navigations INTO a list view, but not on
// in-place changes (category / filters) of the same view. Each navigation is reduced
// to a "visit key" (getUnreadVisitKey): COMPANY_LIST, DOCUMENT_LIST:<companyId>, or
// OTHER. We refetch only when the new key is a list key AND differs from the previous
// key — so:
//   - category change / filter apply → same DOCUMENT_LIST:<companyId> key → skip
//   - company A doc list → company B doc list → key changes → refetch
//   - list → settings/document → back to list → OTHER → list → refetch
//
// Constant-space `while (true)` watcher loop (the standard redux-saga watcher idiom,
// as used by the main pipeline loop below) — not recursion, which would nest a new
// suspended generator per navigation. Cancelled on sign-out with the rest of the
// pipeline.
function* unreadCountsWatcher(): Generator {
  // Initial seed (app load / sign-in).
  yield call(fetchUnreadCounts);
  // Seed prevKey with the current location so the first navigation is compared against
  // where we already are (no duplicate fetch when landing directly on a list route).
  const { pathname } = (yield select(locationSelector)) as { pathname: string };
  let prevKey = getUnreadVisitKey(pathname);

  while (true) {
    const action = (yield take(LOCATION_CHANGE)) as { payload: { location: { pathname: string } } };
    const nextKey = getUnreadVisitKey(action.payload.location.pathname);
    if (nextKey !== prevKey && isListVisitKey(nextKey)) {
      yield call(fetchUnreadCounts);
    }
    prevKey = nextKey;
  }
}

function* worker(dispatch: Dispatch) {
  const context = yield call(createContext);
  yield fork(WSClientFactory(dispatch, context, chatNotificationsMessageHandler(dispatch)));
}

export default function* chatNotifications(dispatch: Dispatch) {
  // Wait for org features to load before checking the flag — saga is forked at
  // app startup before features are available.
  yield take(getOrganizationFeatures.success);

  while (true) {
    // cognitoAuthHandler dispatches signInAction.success before sagas fork, so on
    // page reload it's already been missed — skip take() if userId is already set.
    const existingUserId = (yield select(userIdSelector)) as string | null | undefined;
    if (!existingUserId || ghostIdentities.includes(existingUserId)) {
      yield take(signInAction.success);
    }

    const chatToken = (yield select(chatTokenSelector)) as string | null | undefined;
    const userId = (yield select(userIdSelector)) as string | null | undefined;

    if (!userId || ghostIdentities.includes(userId)) {
      continue;
    }

    // Only run v4 notification pipeline when enable_chat_v2 flag is true
    const isNewChat = (yield select(isNewChatSelector)) as boolean;
    if (!isNewChat) {
      // Legacy v3 mode — skip v4 notifications, wait for sign-out then loop
      yield take(signOutAction.success);
      continue;
    }

    const unreadTask = (yield fork(unreadCountsWatcher)) as Task;
    const wsTask = chatToken ? ((yield fork(worker, dispatch)) as Task) : null;

    yield take(signOutAction.success);
    if (unreadTask) yield cancel(unreadTask);
    if (wsTask) yield cancel(wsTask);
  }
}
